import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CurrencyEur, ShieldCheck, CalendarCheck, Info, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Skeleton } from '../ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RP73-75 (MOA-R5, D29, D31) — onglet « Paiements » du client : les demandes de
 * paiement des entreprises vérifiées et certifiées par l'architecte (jamais les
 * brouillons), ce qui est déjà payé et ce qui reste à payer, la garantie de 5 %
 * expliquée et l'échéancier. Affiché seulement si l'architecte a activé « le client
 * voit les montants » (sinon le serveur refuse : 403).
 * A5 / P4#1 — certifiée ≠ payée : « Déjà payé » ne compte que les paiements notés par
 * l'architecte ; le client signale « J'ai payé » (l'architecte est prévenu et confirme).
 * P4#10 — au téléphone, le détail par entreprise s'affiche en cartes empilées.
 */

const fmtEur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};
const COULEUR_ETAT = {
  a_payer: 'text-[#C75B39]',
  echeance_passee: 'text-[#8B3A3A] font-medium',
  payee: 'text-[#2D6A4F]',
};

function Chiffre({ libelle, valeur, couleur = 'text-[#2C2C2C]', aide }) {
  return (
    <div className="border border-[#E0E0DB] bg-white p-3">
      <p className="text-xs text-[#6B6B6B]">{libelle}</p>
      <p className={`text-lg font-bold ${couleur}`}>{valeur}</p>
      {aide && <p className="text-[11px] text-[#9A9A9A] mt-0.5">{aide}</p>}
    </div>
  );
}

const aujourdhui = () => new Date().toISOString().slice(0, 10);

/** « J'ai payé » : le client indique la date de son virement ; l'architecte confirme. */
function JaiPaye({ chantierId, ligne, onDone }) {
  const [ouvert, setOuvert] = useState(false);
  const [date, setDate] = useState(aujourdhui());
  const [envoi, setEnvoi] = useState(false);
  const envoyer = async () => {
    setEnvoi(true);
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/paiements/${ligne.id}/signaler`,
        { paye_le: date }, { withCredentials: true });
      toast.success('Merci : votre architecte est prévenu et confirmera le paiement.');
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer pour le moment.");
    } finally { setEnvoi(false); }
  };
  if (!ouvert) {
    return (
      <button type="button" onClick={() => setOuvert(true)} data-testid={`jai-paye-${ligne.id}`}
        className="mt-1 inline-flex items-center gap-1 border border-[#2D6A4F] text-[#2D6A4F] text-xs px-2 py-1 hover:bg-[#E8F5E9]">
        <CheckCircle size={14} /> J'ai payé
      </button>
    );
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 sm:justify-end">
      <label className="text-xs text-[#6B6B6B]">Payé le
        <input type="date" value={date} max={aujourdhui()} onChange={(e) => setDate(e.target.value)}
          className="ml-1 border border-[#E0E0DB] px-1 py-0.5 text-xs" data-testid={`jai-paye-date-${ligne.id}`} />
      </label>
      <button type="button" disabled={envoi} onClick={envoyer} data-testid={`jai-paye-envoyer-${ligne.id}`}
        className="bg-[#2D6A4F] text-white text-xs px-2 py-1 disabled:opacity-60">Confirmer</button>
      <button type="button" onClick={() => setOuvert(false)} className="text-xs text-[#6B6B6B] underline">Annuler</button>
    </div>
  );
}

export default function ProjectPaiements({ chantierId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refus, setRefus] = useState(null);

  const charger = useCallback((vivant = { v: true }) => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/paiements`, { withCredentials: true })
      .then(({ data: d }) => { if (vivant.v) setData(d); })
      .catch((err) => {
        if (!vivant.v) return;
        setRefus(err?.response?.status === 403
          ? (err.response.data?.detail || "Les montants de ce chantier ne sont pas partagés avec vous.")
          : "Impossible d'afficher les paiements pour le moment.");
      })
      .finally(() => { if (vivant.v) setLoading(false); });
  }, [chantierId]);

  useEffect(() => {
    const vivant = { v: true };
    setLoading(true);
    charger(vivant);
    return () => { vivant.v = false; };
  }, [charger]);

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;
  if (refus) return <p className="text-sm text-[#6B6B6B] border border-[#E0E0DB] bg-white p-4">{refus}</p>;
  if (!data) return null;

  const t = data.totaux;
  const g = data.garantie;
  return (
    <div className="space-y-5 max-w-4xl" data-testid="project-paiements">
      <div>
        <h2 className="text-lg text-[#2C2C2C] flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
          <CurrencyEur size={20} className="text-[#C75B39]" /> Paiements
        </h2>
        <p className="text-sm text-[#6B6B6B]">
          Chaque mois, les entreprises font une demande de paiement ; votre architecte la vérifie et la certifie.
          Seules les demandes certifiées apparaissent ici. Quand vous avez payé, indiquez-le avec « J'ai payé ».
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Chiffre libelle="Montant des contrats (HT)" valeur={fmtEur(t.contrats_ht)}
          aide={t.avenants_acceptes_ht ? `dont modifications du marché : ${fmtEur(t.avenants_acceptes_ht)}` : null} />
        <Chiffre libelle="Déjà payé (HT)" valeur={fmtEur(t.deja_paye_ht)} couleur="text-[#2D6A4F]"
          aide="Paiements confirmés par votre architecte" />
        <Chiffre libelle="À payer (TTC)" valeur={fmtEur(t.a_payer_prochainement_ttc)} couleur="text-[#C75B39]"
          aide="Certifié par l'architecte, pas encore payé" />
        <Chiffre libelle="Reste à payer (HT)" valeur={fmtEur(t.reste_a_payer_ht)}
          aide="Contrats − déjà payé (garantie comprise)" />
      </div>

      {/* RP75 — la garantie de 5 % expliquée */}
      <section className="border border-[#E0E0DB] bg-white p-4" data-testid="paiements-garantie">
        <h3 className="text-sm font-bold text-[#2C2C2C] flex items-center gap-2">
          <ShieldCheck size={18} className="text-[#1E3A5F]" />
          Garantie de {g.taux_pct ? `${g.taux_pct.toLocaleString('fr-FR')} %` : '5 %'} conservée jusqu'à la levée des réserves (1 an max.)
        </h3>
        <p className="text-sm text-[#4A4A4A] mt-2">{g.explication}</p>
        {g.active && (
          <p className="text-sm mt-2">
            Montant conservé à ce jour : <strong>{fmtEur(g.montant_retenu_ht)} HT</strong>
            {g.liberation_au_plus_tard
              ? <> · rendu au plus tard le <strong>{fmtDate(g.liberation_au_plus_tard)}</strong> (réception le {fmtDate(g.reception_le)})</>
              : <> · rendu après la réception des travaux</>}
          </p>
        )}
      </section>

      {/* RP75 — échéancier */}
      <section className="border border-[#E0E0DB] bg-white" data-testid="paiements-echeancier">
        <div className="px-4 py-3 border-b border-[#E0E0DB] flex items-center gap-2">
          <CalendarCheck size={18} className="text-[#C75B39]" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Échéancier</h3>
        </div>
        {data.echeancier.length === 0 ? (
          <p className="text-sm text-[#9A9A9A] p-4">Aucune demande de paiement certifiée pour l'instant.</p>
        ) : (
          <ul className="divide-y divide-[#E0E0DB]">
            {data.echeancier.map(e => (
              <li key={e.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#2C2C2C]">{e.entreprise || 'Entreprise'}</p>
                  <p className="text-xs text-[#6B6B6B]">
                    {e.lot}{e.numero ? ` · demande n° ${e.numero}` : ''} · Certifiée le {fmtDate(e.certifiee_le)}
                  </p>
                  <p className="text-xs text-[#9A9A9A]">
                    Travaux du mois {fmtEur(e.travaux_du_mois_ht)} HT · garantie conservée {fmtEur(e.garantie_retenue_ht)} HT
                  </p>
                </div>
                <div className="sm:text-right">
                  <p className="text-sm font-bold text-[#2C2C2C]">{fmtEur(e.montant_a_verser_ttc)} TTC</p>
                  <p className={`text-xs ${COULEUR_ETAT[e.etat] || ''}`} data-testid={`echeance-etat-${e.id}`}>
                    {e.etat_libelle}{e.etat !== 'payee' && e.echeance ? ` · échéance ${fmtDate(e.echeance)}` : ''}
                  </p>
                  {e.etat !== 'payee' && (e.paiement_signale_le ? (
                    <p className="text-xs text-[#2D6A4F]">Paiement signalé le {fmtDate(e.paiement_signale_le)} · en attente de confirmation</p>
                  ) : (
                    <JaiPaye chantierId={chantierId} ligne={e} onDone={() => charger()} />
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="text-[11px] text-[#9A9A9A] px-4 py-2 border-t border-[#E0E0DB] flex items-start gap-1">
          <Info size={12} className="mt-0.5 flex-shrink-0" />
          Échéance indicative : {data.delai_paiement_jours} jours après la certification par l'architecte.
        </p>
      </section>

      {data.par_entreprise.length > 0 && (
        <section className="border border-[#E0E0DB] bg-white" data-testid="paiements-par-entreprise">
          {/* P4#10 — téléphone : une carte par entreprise (plus de colonne coupée) */}
          <ul className="sm:hidden divide-y divide-[#E0E0DB]" data-testid="paiements-par-entreprise-cartes">
            {data.par_entreprise.map(l => (
              <li key={`${l.lot}-${l.entreprise}`} className="p-3 text-sm">
                <p className="font-semibold text-[#2C2C2C]">{l.entreprise}</p>
                <p className="text-xs text-[#6B6B6B] mb-2">{l.lot}</p>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <dt className="text-[#6B6B6B]">Contrat HT</dt><dd className="text-right">{fmtEur(l.contrat_ht)}</dd>
                  <dt className="text-[#6B6B6B]">Déjà payé HT</dt><dd className="text-right">{fmtEur(l.deja_paye_ht)}</dd>
                  <dt className="text-[#6B6B6B]">Avancement</dt><dd className="text-right">{Math.round(l.avancement_pct)} %</dd>
                </dl>
              </li>
            ))}
          </ul>
          <table className="hidden sm:table w-full text-sm">
            <thead className="bg-[#F5F5F3] text-xs text-[#6B6B6B]">
              <tr>
                <th className="text-left px-3 py-2">Contrat de l'entreprise</th>
                <th className="text-right px-3 py-2">Contrat HT</th>
                <th className="text-right px-3 py-2">Déjà payé HT</th>
                <th className="text-right px-3 py-2">Avancement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E0E0DB]">
              {data.par_entreprise.map(l => (
                <tr key={`${l.lot}-${l.entreprise}`}>
                  <td className="px-3 py-2"><span className="block">{l.entreprise}</span><span className="text-xs text-[#6B6B6B]">{l.lot}</span></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtEur(l.contrat_ht)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtEur(l.deja_paye_ht)}</td>
                  <td className="px-3 py-2 text-right">{Math.round(l.avancement_pct)} %</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
